import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CodeSnippetPlacementInvalidReason } from '../../Components/CodeEditor/types';
import { DUPLICATE_DRIVE_BASE_DROP_MESSAGE } from '../../Components/CodeEditor/utils/driveBaseSingleton';

export function useSnippetAnnouncements() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const statusTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (statusTimerRef.current) {
        globalThis.clearTimeout(statusTimerRef.current);
      }
    },
    []
  );

  const announce = useCallback((message: string) => {
    if (statusTimerRef.current) {
      globalThis.clearTimeout(statusTimerRef.current);
    }
    setStatus('');
    statusTimerRef.current = globalThis.setTimeout(() => {
      setStatus(message);
      statusTimerRef.current = null;
    }, 0);
  }, []);

  const announceInserted = useCallback(
    (title: string) => {
      announce(t('snippets.status.inserted', 'Inserted {{title}} into the editor.', { title }));
    },
    [announce, t]
  );

  const formatPlacementInstructions = useCallback(
    (title: string, invalidReason?: CodeSnippetPlacementInvalidReason | null) => {
      switch (invalidReason) {
        case 'duplicate-drive-base':
          return t('editor.duplicateDriveBaseDialog.message', DUPLICATE_DRIVE_BASE_DROP_MESSAGE);
        case 'setup-before-imports':
          return t('snippets.status.setupBelowImports', '{{title}} is setup code. Move the cursor below the import section and try again.', {
            title,
          });
        case 'setup-inside-call':
          return t(
            'snippets.status.setupOutsideCall',
            '{{title}} is setup code. Move the cursor outside the surrounding parentheses and try again.',
            { title }
          );
        case 'setup-inside-string':
          return t('snippets.status.setupOutsideString', '{{title}} is setup code. Move the cursor outside the surrounding string and try again.', {
            title,
          });
        case 'setup-after-start':
          return t('snippets.status.setupBeforeStart', '{{title}} is setup code. Move the cursor before RobotBase().start() and try again.', {
            title,
          });
        case 'contextual-outside-compatible-location':
          return t(
            'snippets.status.contextualPlacementOptions',
            '{{title}} can be setup code or an action. Move the cursor outside the surrounding parentheses for setup, or inside an action group or an action-accepting controller or sensor callback, and try again.',
            { title }
          );
        case 'action-outside-compatible-call':
          return t(
            'snippets.status.actionInsideCompatibleCall',
            '{{title}} is an action. Move the cursor inside an action group or an action-accepting controller or sensor callback and try again.',
            { title }
          );
        default:
          return t(
            'snippets.status.placementInstructions',
            '{{title}} selected. Move the cursor to choose an insertion point, then press Ctrl+Enter or Command+Enter to insert. Press Escape to cancel.',
            { title }
          );
      }
    },
    [t]
  );

  const announceRejected = useCallback(
    (title: string, reason?: CodeSnippetPlacementInvalidReason) => {
      announce(
        reason
          ? formatPlacementInstructions(title, reason)
          : t('snippets.status.invalidCursor', '{{title}} cannot be inserted at the current cursor position.', { title })
      );
    },
    [announce, formatPlacementInstructions, t]
  );

  const announcePlacementRequested = useCallback(
    (title: string, invalidReason?: CodeSnippetPlacementInvalidReason | null) => {
      announce(formatPlacementInstructions(title, invalidReason));
    },
    [announce, formatPlacementInstructions]
  );

  const announcePlacementInvalid = useCallback(
    (title: string, reason: CodeSnippetPlacementInvalidReason) => {
      announce(formatPlacementInstructions(title, reason));
    },
    [announce, formatPlacementInstructions]
  );

  const announcePlacementCancelled = useCallback(
    (title: string) => {
      announce(t('snippets.status.placementCancelled', 'Canceled {{title}} insertion.', { title }));
    },
    [announce, t]
  );

  return {
    status,
    announceInserted,
    announceRejected,
    announcePlacementRequested,
    announcePlacementInvalid,
    announcePlacementCancelled,
    formatPlacementInstructions,
  };
}
